import React from "react";
import { createPortal } from "react-dom";

interface ConfirmationModalProps {
  isOpen: boolean;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  onConfirm: () => void;
  onCancel: () => void;
  loading?: boolean;
  variant?: "danger" | "success";
}

export const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  isOpen,
  title,
  message,
  confirmText = "Confirmar",
  cancelText = "Cancelar",
  onConfirm,
  onCancel,
  loading = false,
  variant = "danger",
}) => {
  if (!isOpen) return null;

  // Variant-based styling
  const variantConfig = {
    danger: {
      iconBg: "bg-red-100 dark:bg-red-950 dark:ring-1 dark:ring-red-800",
      icon: "⚠️",
      buttonBg: "bg-red-600 hover:bg-red-500",
      loadingText: "Excluindo...",
    },
    success: {
      iconBg: "bg-green-100 dark:bg-green-950 dark:ring-1 dark:ring-green-800",
      icon: "✅",
      buttonBg: "bg-green-600 hover:bg-green-500",
      loadingText: "Processando...",
    },
  };

  const config = variantConfig[variant];

  return createPortal(
    <div className="fixed inset-0 z-[100] overflow-y-auto">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50 dark:bg-black/70 transition-opacity backdrop-blur-[1px]" />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-2 sm:p-4 text-center">
        <div className="relative overflow-hidden rounded-xl sm:rounded-2xl bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-600 px-4 sm:px-6 pb-4 sm:pb-6 pt-4 sm:pt-5 text-left shadow-xl transition-all w-full max-w-lg sm:my-8">
          <div className="flex items-start">
            <div
              className={`mx-auto flex h-10 w-10 sm:h-12 sm:w-12 flex-shrink-0 items-center justify-center rounded-full ${config.iconBg} sm:mx-0`}
            >
              <span className="text-xl sm:text-2xl">{config.icon}</span>
            </div>
            <div className="mt-0 ml-3 sm:ml-4 flex-1 min-w-0">
              <h3 className="text-base sm:text-lg font-semibold leading-6 text-gray-900 dark:text-white">
                {title}
              </h3>
              <div className="mt-2">
                <p className="text-sm sm:text-base text-gray-500 dark:text-slate-300 break-words">
                  {message}
                </p>
              </div>
            </div>
          </div>
          <div className="mt-4 sm:mt-5 flex flex-col-reverse sm:flex-row sm:flex-row-reverse gap-2 sm:gap-3">
            <button
              type="button"
              onClick={onConfirm}
              disabled={loading}
              className={`inline-flex w-full sm:w-auto justify-center rounded-lg sm:rounded-xl px-4 sm:px-3 py-2.5 sm:py-2 text-sm sm:text-sm font-semibold text-white shadow-sm disabled:opacity-50 disabled:cursor-not-allowed sm:ml-3 ${config.buttonBg}`}
            >
              {loading ? (
                <div className="flex items-center gap-2">
                  <span className="animate-spin text-base sm:text-sm">⏳</span>
                  <span className="text-sm sm:text-sm">{config.loadingText}</span>
                </div>
              ) : (
                confirmText
              )}
            </button>
            <button
              type="button"
              onClick={onCancel}
              disabled={loading}
              className="inline-flex w-full sm:w-auto justify-center rounded-lg sm:rounded-xl bg-white dark:bg-slate-700 px-4 sm:px-3 py-2.5 sm:py-2 text-sm font-semibold text-gray-900 dark:text-slate-100 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-slate-500 hover:bg-gray-50 dark:hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {cancelText}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};
